import { useQuery } from '@tanstack/react-query'

import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { getNotificationReadReceiptsClient } from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'

export const getNotificationUnreadCountKey = (personId: string) => [
  'notificationUnreadCount',
  personId,
]

// Shared with notificationReadReceiptMutations.ts, which reads/writes this
// same cache entry optimistically - keeping one exported type (rather than
// each caller re-declaring its own) means a future shape change fails
// type-checking everywhere it's used instead of silently drifting.
export type NotificationUnreadCountData = {
  count: number
  pusherChannelName: string | undefined
}

export const useNotificationUnreadCount = ({
  enabled,
}: {
  /** Caller-controlled gate (e.g. feature flag AND authenticated). */
  enabled: boolean
}) => {
  const accessToken = useAccessToken()
  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId ?? ''

  return useQuery({
    queryKey: getNotificationUnreadCountKey(personId),
    queryFn: async (): Promise<NotificationUnreadCountData> => {
      const { data } =
        await getNotificationReadReceiptsClient().readReceiptsApiControllerFindCount(
          { personId, read: false },
          buildAxiosRequestConfig({ accessToken: accessToken ?? '' })
        )
      // pusherChannelName rides along on this same response rather than a
      // dedicated endpoint - it's the caller's own real-time channel, and
      // this count is already polled unconditionally whenever the
      // notification center is enabled, making it the earliest point the
      // frontend can learn it. Empty string if Pusher isn't configured
      // server-side (see useNotificationRealtimeUpdates, which treats that
      // the same as "no channel yet").
      return { count: data.count, pusherChannelName: data.pusherChannelName }
    },
    enabled: enabled && !!accessToken && !!personId,
    // Header badge shown on every page. useNotificationRealtimeUpdates
    // invalidates this immediately on a Pusher notification-created event;
    // this interval is just a slow safety net for a missed/dropped event.
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  })
}
